import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { cn } from 'design-system/shadcn/lib/utils'
import { Button } from 'design-system/ui/atoms/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from 'design-system/ui/atoms/dropdown/dropdown-menu'
import { Input } from 'design-system/ui/atoms/dropdown/input'
import { CheckCircle2Icon, PlusIcon, UserIcon } from 'lucide-react'

export const DropdownIcon = ({
  iconComponent,
  color,
}: {
  iconComponent: any
  color: string
}) => {
  const Icon = iconComponent
  return (
    <div
      className={cn(
        'ds-mr-8 ds-flex ds-h-[18px] ds-w-[18px] ds-items-center ds-justify-center ds-rounded-6',
        color,
      )}
    >
      <Icon
        className="ds-h-[14px] ds-w-[14px]"
        strokeWidth={1.5}
        color="white"
      />
    </div>
  )
}

export const DEFAULT_MENU_ITEMS = [
  {
    component_type: 'input',
    placeholder: 'Apply filter...',
    showOnSearch: true,
  },
  { component_type: 'separator', showOnSearch: true },
  { component_type: 'heading', content: 'Status' },
  {
    component_type: 'menu_item_group',
    children: [
      {
        component_type: 'menu_item',
        label: 'All',
        leftIcon: (
          <DropdownIcon iconComponent={UserIcon} color="ds-bg-green-800" />
        ),
        rightIcon: null,
      },
      {
        component_type: 'menu_item',
        label: 'Warning',
        rightIcon: '⇧⌘P',
      },
    ],
  },
  { component_type: 'separator' },
  {
    component_type: 'cta',
    label: 'New Field',
    rightIcon: <PlusIcon className="ds-mr-2 ds-h-[16px] ds-w-[16px]" />,
  },
]

interface DropdownItem {
  component_type: string
  searchable?: boolean
  label?: string
  value?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode | string
  content?: string
  children?: DropdownItem[]
  placeholder?: string
  showOnSearch?: boolean
}

interface SearchableDropdownProps {
  selectedValue: string
  onChange: (value: string) => void
  dataItems: DropdownItem[]
  defaultOpen?: boolean
  children?: React.ReactNode
  onNoContentLabel?: string
  dialogClassName?: string
  defaultButtonProps?: React.ComponentProps<typeof Button>
}
export function SearchableDropdown({
  selectedValue,
  onChange,
  dataItems = DEFAULT_MENU_ITEMS,
  defaultOpen = false,
  children,
  onNoContentLabel = 'No results found',
  dialogClassName,
}: SearchableDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItem, setSelectedItem] = useState<string | null>(
    selectedValue || null,
  )

  const [open, setOpen] = useState(defaultOpen)
  const inputRef = useRef<HTMLInputElement>(null)

  const isChildrenString = typeof children === 'string'

  const filteredItems = dataItems.map((item) => {
    if (item.component_type === 'menu_item_group') {
      return {
        ...item,
        children: item.children?.filter((child) =>
          child.label?.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      }
    }
    return item
  })

  const hasFilteredItems = filteredItems.some(
    (item) =>
      item.component_type === 'menu_item_group' &&
      item.children &&
      item.children.length > 0,
  )

  const ItemDropdown = useCallback(
    ({
      item,
      searchTerm,
      selectedItem,
    }: {
      item: DropdownItem
      searchTerm: string
      selectedItem: string | null
    }) => {
      if (item.component_type === 'input') {
        return (
          <Input
            ref={inputRef}
            value={searchTerm}
            onChange={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setSearchTerm(e.target.value)
            }}
            placeholder={item.placeholder}
            className="ds-border-none ds-outline-none"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          />
        )
      }

      // Only show menu items if we're not searching or if this is a menu group with matches
      if (
        searchTerm &&
        item.component_type !== 'menu_item_group' &&
        !item.showOnSearch
      ) {
        return null
      }

      if (item.component_type === 'separator') {
        return <DropdownMenuSeparator />
      }
      if (item.component_type === 'heading') {
        return <DropdownMenuLabel>{item.content}</DropdownMenuLabel>
      }
      if (
        item.component_type === 'menu_item_group' &&
        item.children &&
        item.children.length > 0
      ) {
        return (
          <DropdownMenuGroup>
            {item.children.map((child: DropdownItem, index: number) => (
              <DropdownMenuItem
                key={index}
                onSelect={() => {
                  setSelectedItem(child.value || null)
                  onChange(child.value || '')
                }}
                className="ds-flex ds-items-center ds-justify-between"
              >
                <div className="ds-flex ds-items-center ds-gap-2">
                  {child.leftIcon}
                  <span>{child.label}</span>
                </div>
                <div className="ds-flex ds-items-center">
                  {selectedItem === child.value && (
                    <CheckCircle2Icon
                      className="ds-mr-4 ds-h-[16px] ds-w-[16px]"
                      strokeWidth={1.5}
                    />
                  )}
                  {child.rightIcon && (
                    <DropdownMenuShortcut>
                      {child.rightIcon}
                    </DropdownMenuShortcut>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuGroup>
        )
      }
      if (item.component_type === 'cta') {
        return (
          <DropdownMenuItem className="ds-flex ds-items-center ds-justify-between">
            <div>
              {item.leftIcon}
              <span>{item.label}</span>
            </div>
            <div>{item.rightIcon}</div>
          </DropdownMenuItem>
        )
      }
      return null
    },
    [],
  )

  useEffect(() => {
    if (selectedValue !== selectedItem) {
      setSelectedItem(selectedValue)
    }
  }, [selectedValue])

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        setOpen(open)
        if (open) {
          requestAnimationFrame(() => {
            inputRef.current?.focus()
          })
        }
      }}
      open={open}
    >
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className={clsx('ds-w-56 ds-border-white-200', dialogClassName)}
      >
        {filteredItems.map((item, index) => (
          <ItemDropdown
            key={index}
            item={item}
            searchTerm={searchTerm}
            selectedItem={selectedItem}
          />
        ))}
        {/* Show filtered items or no content message */}
        {!hasFilteredItems
          ? searchTerm &&
            onNoContentLabel && (
              <DropdownMenuItem disabled>{onNoContentLabel}</DropdownMenuItem>
            )
          : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
